/**
 * useSloData.ts — Hook for nself-slo-tracker plugin (port 3835)
 *
 * Purpose:    Fetch and manage SLO definitions, error budgets, and burn-rate alerts.
 * Inputs:     None (reads from plugin backend port 3835)
 * Outputs:    { state, data, error, refresh, saveSlo, deleteSlo }
 * Constraints: Client-side only; uses pluginFetch; AsyncState for UI
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useCallback, useEffect, useState } from 'react'
import { pluginFetch } from '../lib/plugin-client'
import type { SentryError } from '../lib/sentry-error'
import type { AsyncState } from '../components/AsyncScreen'

const PORT = 3835

export type SloWindow = '7d' | '30d' | '90d'

export interface SloDefinition {
  id: string
  name: string
  metricQuery: string
  targetPercent: number
  window: SloWindow
  createdAt: string
}

export interface ErrorBudgetPoint {
  /** ISO timestamp */
  time: string
  /** Remaining error budget percentage (0-100) */
  budgetPercent: number
  /** Burn rate multiplier (1.0 = exactly on pace) */
  burnRate: number
}

export interface BurnRateAlert {
  sloId: string
  sloName: string
  currentBurnRate: number
  threshold: number
  severity: 'warning' | 'critical'
  triggeredAt: string
}

export interface SloData {
  slos: SloDefinition[]
  /** Keyed by sloId */
  errorBudgets: Record<string, ErrorBudgetPoint[]>
  burnRateAlerts: BurnRateAlert[]
}

export function useSloData() {
  const [state, setState] = useState<AsyncState>('loading')
  const [data, setData] = useState<SloData | null>(null)
  const [error, setError] = useState<SentryError | undefined>()

  const load = useCallback(async () => {
    setState('loading')
    setError(undefined)
    if (!navigator.onLine) { setState('offline'); return }
    const result = await pluginFetch<SloData>(PORT, '/api/slo/summary')
    if (result.ok) {
      setData(result.value)
      setState(result.value.slos.length === 0 ? 'empty' : 'success')
    } else {
      const e = result.error
      if (e.type === 'bundle_required') setState('permission-denied')
      else if (e.type === 'rate_limit') setState('rate-limited')
      else if (e.type === 'plugin_offline') setState('offline')
      else setState('error')
      setError(e)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const saveSlo = useCallback(
    async (slo: Omit<SloDefinition, 'id' | 'createdAt'> & { id?: string }): Promise<boolean> => {
      const isNew = !slo.id
      const result = await pluginFetch(
        PORT,
        isNew ? '/api/slo' : `/api/slo/${slo.id}`,
        { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(slo) },
      )
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  const deleteSlo = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await pluginFetch(PORT, `/api/slo/${id}`, { method: 'DELETE' })
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  return { state, data, error, refresh: load, saveSlo, deleteSlo }
}
